import type { paths } from '@s2w/api-types';
import { adminApiClient, type AdminApiClient } from './client';

export type ClubListResponse =
  paths['/admin/sports/{sportId}/clubs']['get']['responses'][200]['content']['application/json'];
export type Club = ClubListResponse['items'][number];

export type AttachClubImagePayload = NonNullable<
  paths['/admin/sports/{sportId}/clubs/{clubId}/image']['patch']['requestBody']
>['content']['application/json'];
export type ClubImageKind = AttachClubImagePayload['kind'];
export type AttachClubImageResponse =
  paths['/admin/sports/{sportId}/clubs/{clubId}/image']['patch']['responses'][200]['content']['application/json'];

/**
 * Both of a club's jersey-number colours at once (CAE-1615) — `primaryColour`
 * is the outer stroke a player's shirt number is drawn in and
 * `secondaryColour` the inner one.
 *
 * The pair is one value, which is why the contract has no field-at-a-time
 * form: what is sent is what the club is left holding, and a null is the club
 * leaving that stroke to the game's default jersey-number styling rather than
 * a field the caller declined to mention.
 */
export type SetClubColoursPayload = NonNullable<
  paths['/admin/sports/{sportId}/clubs/{clubId}/colours']['patch']['requestBody']
>['content']['application/json'];
export type SetClubColoursResponse =
  paths['/admin/sports/{sportId}/clubs/{clubId}/colours']['patch']['responses'][200]['content']['application/json'];

export interface ClubsApi {
  /** A Sport's clubs by name with their Team Logo and Generic Jersey, and how many clubs lack each. */
  list: (sportId: string) => Promise<ClubListResponse>;
  /** Makes an asset from the asset library the club's logo or jersey, replacing the one that held the role. */
  attachImage: (
    sportId: string,
    clubId: string,
    payload: AttachClubImagePayload,
  ) => Promise<AttachClubImageResponse>;
  /**
   * Holds the club to both jersey-number colours, the Operator's override of
   * what the club was seeded with — and answers with the club carrying the
   * pair it now holds.
   */
  setColours: (
    sportId: string,
    clubId: string,
    payload: SetClubColoursPayload,
  ) => Promise<SetClubColoursResponse>;
}

export const createClubsApi = (client: AdminApiClient = adminApiClient): ClubsApi => ({
  list: async (sportId) => client.request<ClubListResponse>(`/admin/sports/${sportId}/clubs`),
  attachImage: async (sportId, clubId, payload) =>
    client.request<AttachClubImageResponse>(`/admin/sports/${sportId}/clubs/${clubId}/image`, {
      method: 'PATCH',
      body: payload,
    }),
  setColours: async (sportId, clubId, payload) =>
    client.request<SetClubColoursResponse>(`/admin/sports/${sportId}/clubs/${clubId}/colours`, {
      method: 'PATCH',
      body: payload,
    }),
});

export const clubsApi = createClubsApi();
